import { arrayToObject, EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import { getInsuranceItems, isCheckoutPostPurchaseInsurance } from '../insurance'
import getCheckoutSelectedQuotes from '../getCheckoutSelectedQuote'
import { getOrderInsuranceUpdateQuoteKey } from 'lib/order/orderInsuranceUtils'
import getTotalsWithoutInsurance from '../../payment/getTotalsWithoutInsurance'
import getLoyaltyInsuranceProductType from 'loyalty/lib/getLoyaltyInsuranceProductType'
import { selectShouldUseInsuranceMemberPrice } from '../luxPlusSubscription'
import getCanRedeemInsuranceLoyaltyBenefit from 'checkout/selectors/loyalty/getCanRedeemInsuranceLoyaltyBenefit'
import { capitalise } from 'lib/string/stringUtils'
import { PRODUCT_ID_PASSPORT_CARD, INSURANCE_TYPE_PASSPORT_CARD } from 'constants/insurance'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'
import {
  getPassportCardQuoteRequestKey,
  getPassportCardSelectedOptionalBenefitCodes,
  type PassportCardQuoteRequestParams,
  travellerDetailsFromInsuranceTravellers,
  travellerDetailsFromOccupants,
} from 'lib/insurance/passportCardQuoteUtils'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getPassportCardQuoteItems from './getPassportCardQuoteItems'
import { getCheckoutInsuranceCountries, getCheckoutInsuranceDates, getCheckoutInsuranceTravellers } from 'checkout/selectors/request/insurance'
import getPassportCardCheckoutInsuranceCoverAmount from 'checkout/selectors/request/getPassportCardCheckoutInsuranceCoverAmount'
import getCheckoutQuoteSessionId from 'checkout/selectors/request/getCheckoutQuoteSessionId'

function getPassportCardSelectedOptionalBenefitCodeSet(
  item: App.Checkout.InsuranceItem,
  proposal: App.PassportCardProposal,
): Set<string> {
  if (item.passportCardOptionalBenefitCodes !== undefined) {
    return new Set(item.passportCardOptionalBenefitCodes)
  }

  return new Set(getPassportCardSelectedOptionalBenefitCodes(proposal.optionalBenefits))
}

function getPassportCardTotalPrice(
  item: App.Checkout.InsuranceItem,
  proposal: App.PassportCardProposal,
): number {
  const selectedOptionalBenefitCodes = getPassportCardSelectedOptionalBenefitCodeSet(item, proposal)

  return proposal.optionalBenefits.reduce((total, benefit) => {
    if (selectedOptionalBenefitCodes.has(benefit.code)) {
      return total + benefit.price.consumerPrice
    }
    return total
  }, proposal.price.consumerPrice)
}

/**
 * Retrieves a record of item id -> update quote for all post-purchase
 * insurance items currently in the checkout
 */
const getCheckoutSelectedQuoteUpdates = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => getTotalsWithoutInsurance(state).data.price,
  (state: App.State) => state.insurance.updateQuotes,
  (orderItems, items, price, updateQuotes): Record<string, App.InsuranceUpdateQuote | undefined> => {
    return arrayToObject(items,
      item => item.itemId,
      item => {
        const orderItem = orderItems?.find(orderItem => orderItem.id === item.orderItemId)
        if (orderItem) {
          const key = getOrderInsuranceUpdateQuoteKey({
            itemId: orderItem.id,
            startDate: item.startDate ?? orderItem.startDate,
            endDate: item.endDate ?? orderItem.endDate,
            travellers: item.travellers ?? orderItem.travellers,
            coverageAmount: item.coverageAmount ?? orderItem.coverAmount + price,
          })

          return updateQuotes[key]?.quote
        }
      })
  })

/**
 * Retrieves a record of item id -> PassportCard update quote for all
 * post-purchase PassportCard insurance items currently in the checkout
 */
const getCheckoutSelectedPassportCardQuoteUpdates = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => getTotalsWithoutInsurance(state).data.price,
  (state: App.State) => state.passportCardInsurance.updateQuotes,
  (orderItems, items, price, passportCardUpdateQuotes): Record<string, App.PassportCardInsuranceUpdateQuote | undefined> => {
    return arrayToObject(items,
      item => item.itemId,
      item => {
        const orderItem = orderItems?.find(orderItem => orderItem.id === item.orderItemId)
        if (orderItem?.insuranceType === INSURANCE_TYPE_PASSPORT_CARD) {
          const key = getOrderInsuranceUpdateQuoteKey({
            itemId: orderItem.id,
            startDate: item.startDate ?? orderItem.startDate,
            endDate: item.endDate ?? orderItem.endDate,
            travellers: item.travellers ?? orderItem.travellers,
            coverageAmount: item.coverageAmount ?? orderItem.coverAmount + price,
          })

          return passportCardUpdateQuotes[key]?.quote
        }
      })
  })

const getInsuranceItemsView = createSelector(
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => state.insurance.products,
  (state: App.State) => getCheckoutSelectedQuotes(state),
  (state: App.State) => getCheckoutSelectedQuoteUpdates(state),
  (state: App.State) => getCheckoutSelectedPassportCardQuoteUpdates(state),
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (state: App.State) => selectShouldUseInsuranceMemberPrice(state),
  (state: App.State) => getCanRedeemInsuranceLoyaltyBenefit(state),
  (state: App.State) => state.geo.insuranceProductName,
  (state: App.State) => isCheckoutPostPurchaseInsurance(state),
  (state: App.State) => state.passportCardInsurance.quotes,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getPassportCardCheckoutInsuranceCoverAmount(state),
  (state: App.State) => getPassportCardQuoteItems(state),
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (state: App.State) => getCheckoutInsuranceDates(state),
  (state: App.State) => getCheckoutInsuranceCountries(state),
  (state: App.State) => getIsLoyaltyEnabled(state) ? state.loyalty.account.data?.tier : undefined,
  (state: App.State) => state.auth.account.agentId,
  (state: App.State) => getCheckoutQuoteSessionId(state),
  (
    insuranceItems,
    products,
    quotes,
    updateQuotes,
    passportCardUpdateQuotes,
    orderInsuranceItems = EmptyArray,
    selectShouldUseInsuranceMemberPrice,
    canRedeemInsuranceLoyaltyBenefit,
    insuranceName,
    containsFlighsWithEmbeddedInsurance,
    passportCardQuotes,
    currentCurrency,
    currentRegionCode,
    passportCardCheckoutInsuranceCoverAmount,
    passportCardQuoteItems,
    checkoutTravellers,
    checkoutDates,
    checkoutCountries,
    loyaltyTier,
    spoofingAgentId,
    sessionId,
  ): App.WithDataStatus<Array<App.Checkout.InsuranceItemView>> => {
    const intl = getCurrentIntl()
    let hasRequiredData = true
    const fallbackTravellerDetails = travellerDetailsFromOccupants(checkoutTravellers)
    const itemViews = insuranceItems.map((item): App.Checkout.InsuranceItemView | null => {
      let title = intl.formatMessage(
        {
          id: 'gEQvw_-fXFM_tN6KeX7_',
          defaultMessage: 'Travel {insuranceName}',
          description: 'Heading for the travel insurance product section in the checkout flow',
        },
        { insuranceName: capitalise(insuranceName) },
      )
      if (item.insuranceType === 'cfmr') {
        title = containsFlighsWithEmbeddedInsurance ? intl.formatMessage(
          bookingProtectionGlossaryMessages.luxuryEscapesProtect,
        ) : intl.formatMessage(
          {
            id: 'NCsrRo4PUPacWPdfisea',
            defaultMessage: 'Cancellation {insuranceName}',
            description: 'Heading for the cancellation insurance product section in the checkout flow',
          },
          { insuranceName: capitalise(insuranceName) },
        )
      }
      if (item.productId) {
        // PassportCard uses a different quotes and products logic
        if (item.productId === PRODUCT_ID_PASSPORT_CARD) {
          const travellersDetails = item.passportCardTravellers?.length
            ? travellerDetailsFromInsuranceTravellers(item.passportCardTravellers)
            : fallbackTravellerDetails

          const title = intl.formatMessage(
            {
              id: 'f9xwQdzJwGxwvbCI48Gr',
              defaultMessage: 'Travel Insurance',
              description: 'Title for PassportCard travel insurance in checkout insurance item view',
            },
          )

          const requestParams: PassportCardQuoteRequestParams = {
            travellersDetails,
            destinationCountries: item.countries ?? (checkoutCountries.length ? checkoutCountries : []),
            tripStartDate: item.startDate ?? checkoutDates.startDate ?? '',
            tripEndDate: item.endDate ?? checkoutDates.endDate ?? '',
            productId: PRODUCT_ID_PASSPORT_CARD,
            totalPrice: passportCardCheckoutInsuranceCoverAmount,
            countryCode: currentRegionCode,
            currency: currentCurrency,
            sessionId,
            items: passportCardQuoteItems,
            filters: {
              cancellation_limit: item.coverageAmount,
              excess_limit: item.excessAmount,
            },
            optionalCovers: item.passportCardOptionalCovers,
            loyaltyTier,
            spoofingAgentId,
          }

          const requestKey = getPassportCardQuoteRequestKey(requestParams)
          const passportCardQuote = requestKey ? passportCardQuotes[requestKey] : undefined
          const proposal = passportCardQuote?.proposals[0]
          const quoteId = item.quoteId ?? passportCardQuote?.quoteId

          if (!proposal || !quoteId) {
            hasRequiredData = false
            return null
          }

          const baseLoyaltyPrice = proposal.loyaltyPrice ?? proposal.price.consumerPrice
          const optionalBenefitsTotal = getPassportCardTotalPrice(item, proposal) - proposal.price.consumerPrice
          const loyaltyTotal = baseLoyaltyPrice + optionalBenefitsTotal
          const publicTotal = getPassportCardTotalPrice(item, proposal)

          return {
            title,
            item,
            quoteId,
            productId: item.productId,
            name: proposal.communications.title || title,
            policyNames: [],
            startDate: item.startDate ?? checkoutDates.startDate ?? '',
            endDate: item.endDate ?? checkoutDates.endDate ?? '',
            destinationCountries: item.countries?.length
              ? item.countries
              : checkoutCountries.length ? checkoutCountries : [],
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              // No selectShouldUseInsuranceMemberPrice guard here (unlike CoverGenius) because
              // PassportCard does not support LuxPlus+ member pricing - loyalty is the only discount type.
              savingsAmount: canRedeemInsuranceLoyaltyBenefit ? publicTotal - loyaltyTotal : 0,
            },
            totals: {
              price: canRedeemInsuranceLoyaltyBenefit ? loyaltyTotal : publicTotal,
              memberPrice: 0,
              value: publicTotal,
              surcharge: 0,
              taxesAndFees: 0,
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            benefits: proposal.communications.covers,
            mobileAppPrice: 0,
            coverageAmount: item.coverageAmount ?? 0,
          }
        }

        const product = products[item.productId]
        const quote = quotes[item.itemId]
        hasRequiredData = !!(product && quote)
        if (product && quote) {
          const selectedPolicyIds = new Set(item.policyIds)
          const selectedPolicyNames = product.policyOptions
            .filter(option => selectedPolicyIds.has(option.policy))
            .map(option => option.name)
          const policyNames = product.policyNames.length ? product.policyNames : selectedPolicyNames
          let benefits: Array<string> = []
          const isCustomizedProtectionSelected = selectedPolicyNames.length > 0
          if (isCustomizedProtectionSelected) {
            benefits = item.policyIds?.flatMap((selectedPolicyId) => {
              return product.policyOptions?.find((p) => p.policy === selectedPolicyId)?.keyBenefits?.map(benefit => benefit.label) ?? []
            }) ?? []
          } else {
            benefits = product.keyBenefits.map((benefit) => benefit.label)
          }

          const total = canRedeemInsuranceLoyaltyBenefit ? quote.loyaltyTotal : quote.total
          const useMobilePrice = quote.mobileAppOnlyPrice && !canRedeemInsuranceLoyaltyBenefit

          return {
            title,
            item,
            quoteId: quote.id,
            productId: item.productId,
            name: product.name,
            policyNames,
            startDate: quote.startDate,
            endDate: quote.endDate,
            destinationCountries: quote.destinationCountries,
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              savingsAmount: canRedeemInsuranceLoyaltyBenefit && !selectShouldUseInsuranceMemberPrice ? quote.total - quote.loyaltyTotal : 0,
            },
            totals: {
              price: useMobilePrice ? quote.mobileAppOnlyPrice : total,
              memberPrice: selectShouldUseInsuranceMemberPrice ? quote.luxPlusTotal : 0,
              value: quote.total,
              surcharge: 0,
              taxesAndFees: 0,
              ...(useMobilePrice && quote.mobileAppOnlyPrice > 0 && {
                mobileAppDiscount: {
                  amount: quote.total - quote.mobileAppOnlyPrice,
                  percentage: 20,
                },
              }),
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            benefits,
            mobileAppPrice: useMobilePrice ? quote.mobileAppOnlyPrice ?? 0 : 0,
            coverageAmount: quote.coverageAmount,
          }
        }
      } else if (item.orderItemId) {
        const orderItem = orderInsuranceItems.find(orderItem => orderItem.id === item.orderItemId)!
        const passportCardQuote = passportCardUpdateQuotes[item.itemId]

        if (passportCardQuote) {
          hasRequiredData = true
          return {
            title,
            item,
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              savingsAmount: 0,
            },
            updateQuoteId: passportCardQuote.proposalId,
            productId: orderItem.idProduct!,
            name: orderItem.productName,
            policyNames: orderItem.policyNames ?? [],
            startDate: passportCardQuote.startDate,
            endDate: passportCardQuote.endDate,
            destinationCountries: orderItem.destinations ?? [],
            totals: {
              price: passportCardQuote.priceDiff.amount,
              memberPrice: passportCardQuote.priceDiff.amount,
              value: passportCardQuote.newPrice.amount,
              surcharge: 0,
              taxesAndFees: 0,
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            coverageAmount: passportCardQuote.coverageAmount,
            benefits: [],
            mobileAppPrice: 0,
          }
        }

        const updateQuote = updateQuotes[item.itemId]
        hasRequiredData = !!updateQuote
        if (updateQuote) {
          return {
            title,
            item,
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              savingsAmount: 0,
            },
            updateQuoteId: updateQuote.id,
            productId: orderItem.idProduct!,
            name: orderItem.productName,
            policyNames: orderItem.policyNames ?? [],
            startDate: updateQuote.startDate,
            endDate: updateQuote.endDate,
            destinationCountries: orderItem.destinations ?? [],
            totals: {
              price: updateQuote.totalPriceDiff,
              // note: member price not implemented on update quotes yet?
              memberPrice: updateQuote.totalPriceDiff,
              value: updateQuote.total,
              surcharge: 0,
              taxesAndFees: 0,
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            coverageAmount: updateQuote.coverageAmount,
            benefits: [],
            mobileAppPrice: 0,
          }
        }
      }

      return null
    })

    return {
      hasRequiredData,
      data: nonNullable(itemViews),
    }
  },
)

export default getInsuranceItemsView
